/**
 * Pure serialization helpers between the builder's Date & Time element state
 * and the custom-field `dateTimeValidation` payload + canvas preview token.
 *
 * Encoding rules (see plan 2026-07-22-date-time-picker-field):
 * - Weekdays serialize to numeric array 0=Sun .. 6=Sat, sorted ascending.
 * - Times serialize to 24h `HH:MM`. No `timezone` is ever emitted (server-added).
 * - buffer/rolling coerce empty -> 0 (never block).
 */

export type DateMode = "any" | "future_only" | "past_only" | "range";
export type TimeMode = "any" | "range";
export type FutureSub = "any_future" | "buffer" | "rolling";
export type CollectType = "DATE" | "TIME" | "DATE_TIME";
export type TimeFormat = "24" | "12";
export type DefaultTimeOption = "none" | "current" | "custom";
export type Meridian = "AM" | "PM";

/** Mon-first weekday map as rendered in the design. */
export interface WeekdayMap {
  Mon?: boolean;
  Tue?: boolean;
  Wed?: boolean;
  Thu?: boolean;
  Fri?: boolean;
  Sat?: boolean;
  Sun?: boolean;
}

/** Payload attached to the custom-field API body. */
export interface DateTimeValidation {
  mode: DateMode;
  daysAllowed?: number[];
  bufferDays?: number;
  rollingDays?: number;
  rangeMin?: string;
  rangeMax?: string;
  /** Server-added on response only; never sent by the client. */
  timezone?: string;
}

/** Subset of the builder element the serializer reads. */
export interface DateTimeElement {
  dataType?: string;
  dateTimeValidation?: DateTimeValidation;
  dateMode?: DateMode;
  futureSub?: FutureSub;
  bufferDays?: number | string;
  rollingDays?: number | string;
  dateRangeMin?: string;
  dateRangeMax?: string;
  days?: WeekdayMap;
  timeMode?: TimeMode;
  timeRangeStart?: string;
  timeRangeStartMeridian?: Meridian;
  timeRangeEnd?: string;
  timeRangeEndMeridian?: Meridian;
  timeFormat?: TimeFormat;
  format?: string;
  separator?: string;
  placeholder?: string;
  defaultTime?: DefaultTimeOption;
  customTime?: string;
  customTimeMeridian?: Meridian;
}

/** 0=Sun .. 6=Sat, keyed by Mon-first display label. */
const WEEKDAY_TO_NUMBER: Record<keyof WeekdayMap, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const ALL_DAYS: number[] = [0, 1, 2, 3, 4, 5, 6];
const DATE_PLACEHOLDER_FORMATS = ["MM-DD-YYYY", "DD-MM-YYYY", "YYYY-MM-DD"];
const DATE_PLACEHOLDER_SEPARATORS = ["-", "/", "."];
const TIME_PLACEHOLDER_FORMATS = ["HH:MM", "HH:MM AM"];
const LEGACY_GENERIC_DATE_PLACEHOLDERS = ["Select date"];
const LEGACY_GENERIC_TIME_PLACEHOLDERS = ["Select time"];
const DATE_TIME_DATA_TYPES = ["DATE", "TIME", "DATE_TIME"] as const;
const DATE_TIME_BUILDER_STATE_KEYS = [
  "dateMode",
  "futureSub",
  "bufferDays",
  "rollingDays",
  "dateRangeMin",
  "dateRangeMax",
  "days",
  "timeMode",
  "timeRangeStart",
  "timeRangeStartMeridian",
  "timeRangeEnd",
  "timeRangeEndMeridian",
  "daysAllowed",
] as const;

function normalizeGeneratedPreviewPlaceholder(value: string): string {
  return value.trim().replace(/\s*([./-])\s*/g, "$1").replace(/\s+/g, " ");
}

const GENERATED_DATE_PLACEHOLDERS = new Set(
  [
    ...DATE_PLACEHOLDER_FORMATS.flatMap((format) =>
      DATE_PLACEHOLDER_SEPARATORS.map((separator) =>
        normalizeGeneratedPreviewPlaceholder(format.replace(/-/g, separator))
      )
    ),
    ...LEGACY_GENERIC_DATE_PLACEHOLDERS.map((placeholder) =>
      normalizeGeneratedPreviewPlaceholder(placeholder)
    ),
  ]
);

const GENERATED_TIME_PLACEHOLDERS = new Set([
  ...TIME_PLACEHOLDER_FORMATS.map((format) => normalizeGeneratedPreviewPlaceholder(format)),
  ...LEGACY_GENERIC_TIME_PLACEHOLDERS.map((placeholder) =>
    normalizeGeneratedPreviewPlaceholder(placeholder)
  ),
]);

const GENERATED_DATE_TIME_PLACEHOLDERS = new Set(
  [
    ...DATE_PLACEHOLDER_FORMATS.flatMap((format) =>
      DATE_PLACEHOLDER_SEPARATORS.flatMap((separator) =>
        TIME_PLACEHOLDER_FORMATS.map((time) =>
          normalizeGeneratedPreviewPlaceholder(`${format.replace(/-/g, separator)} ${time}`)
        )
      )
    ),
    ...LEGACY_GENERIC_DATE_PLACEHOLDERS.flatMap((date) =>
      LEGACY_GENERIC_TIME_PLACEHOLDERS.map((time) =>
        normalizeGeneratedPreviewPlaceholder(`${date} ${time}`)
      )
    ),
  ]
);

export function formatDatePreview(format = "YYYY-MM-DD", separator = "-"): string {
  return format.replace(/-/g, separator) || "YYYY-MM-DD";
}

function formatTimePreview(timeFormat: TimeFormat = "24"): string {
  return timeFormat === "12" ? "HH:MM AM" : "HH:MM";
}

function formatTimeInputPreview(): string {
  return "HH:MM";
}

export function isGeneratedPreviewPlaceholder(value: string, collect: CollectType): boolean {
  const normalized = normalizeGeneratedPreviewPlaceholder(value);
  if (collect === "TIME") return GENERATED_TIME_PLACEHOLDERS.has(normalized);
  if (collect === "DATE") return GENERATED_DATE_PLACEHOLDERS.has(normalized);
  return (
    GENERATED_DATE_PLACEHOLDERS.has(normalized) ||
    GENERATED_TIME_PLACEHOLDERS.has(normalized) ||
    GENERATED_DATE_TIME_PLACEHOLDERS.has(normalized)
  );
}

export function getDatePreviewPlaceholder(element: DateTimeElement): string {
  const collect = (element.dataType as CollectType) ?? "DATE";
  const placeholder = element.placeholder?.trim();
  if (placeholder && !isGeneratedPreviewPlaceholder(placeholder, collect)) {
    return placeholder;
  }
  return formatDatePreview(element.format, element.separator);
}

export function getTimePreviewPlaceholder(element: DateTimeElement): string {
  const collect = (element.dataType as CollectType) ?? "TIME";
  const placeholder = element.placeholder?.trim();
  if (
    collect === "TIME" &&
    placeholder &&
    !isGeneratedPreviewPlaceholder(placeholder, "DATE_TIME")
  ) {
    return placeholder;
  }
  return formatTimeInputPreview();
}

/** Coerce a possibly-empty numeric input to an int; empty/invalid -> 0. */
export function toInt(value: number | string | undefined): number {
  if (typeof value === "number") return Number.isFinite(value) ? Math.trunc(value) : 0;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/** Mon-first weekday map -> sorted numeric array (0=Sun..6=Sat). */
export function daysToPayload(days: WeekdayMap | undefined): number[] {
  if (!days) return [];
  return (Object.keys(WEEKDAY_TO_NUMBER) as Array<keyof WeekdayMap>)
    .filter((key) => days[key] === true)
    .map((key) => WEEKDAY_TO_NUMBER[key])
    .sort((a, b) => a - b);
}

/** Numeric array (0=Sun..6=Sat) -> Mon-first weekday map. */
export function payloadToDays(arr: number[] | undefined): WeekdayMap {
  const set = new Set(arr ?? []);
  const map: WeekdayMap = {};
  (Object.keys(WEEKDAY_TO_NUMBER) as Array<keyof WeekdayMap>).forEach((key) => {
    map[key] = set.has(WEEKDAY_TO_NUMBER[key]);
  });
  return map;
}

/** Convert a display time to canonical 24h `HH:MM`. */
export function to24(time: string | undefined, meridian?: Meridian, fmt: TimeFormat = "24"): string {
  if (!time || time.trim() === "") return "00:00";
  const [rawH, rawM = "0"] = time.split(":");
  let hours = toInt(rawH);
  const minutes = toInt(rawM);
  if (fmt === "12") {
    hours = hours % 12;
    if (meridian === "PM") hours += 12;
  }
  const hh = String(Math.max(0, Math.min(23, hours))).padStart(2, "0");
  const mm = String(Math.max(0, Math.min(59, minutes))).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function from24(
  time: string | undefined,
  fmt: TimeFormat = "24"
): { value?: string; meridian?: Meridian } {
  const parsed = parseStrictTimeParts(time);
  if (!parsed) return {};

  const { hours, minutes } = parsed;
  if (fmt === "24") {
    return {
      value: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    };
  }

  const meridian: Meridian = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return {
    value: `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    meridian,
  };
}

function isDigits(value: string): boolean {
  return value.length > 0 && [...value].every((char) => char >= "0" && char <= "9");
}

export function parseStrictTimeParts(
  time: string | undefined
): { hours: number; minutes: number } | undefined {
  const value = String(time ?? "").trim();
  const parts = value.split(":");
  if (parts.length !== 2) return undefined;

  const [hourPart, minutePart] = parts;
  if (hourPart.length < 1 || hourPart.length > 2 || minutePart.length !== 2) {
    return undefined;
  }
  if (!isDigits(hourPart) || !isDigits(minutePart)) return undefined;

  const hours = Number(hourPart);
  const minutes = Number(minutePart);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return undefined;
  if (minutes < 0 || minutes > 59) return undefined;
  return { hours, minutes };
}

/** True when the display value is a valid custom default time for the selected clock. */
export function isValidTimeValue(time: string | undefined, fmt: TimeFormat = "24"): boolean {
  const parsed = parseStrictTimeParts(time);
  if (!parsed) return false;
  const { hours } = parsed;
  return fmt === "12" ? hours >= 1 && hours <= 12 : hours >= 0 && hours <= 23;
}

export function toTimePickerTimestamp(
  time: string | undefined,
  fmt: TimeFormat = "24"
): number | null {
  if (!isValidTimeValue(time, fmt)) return null;
  const parsed = parseStrictTimeParts(time);
  if (!parsed) return null;
  return new Date(2000, 0, 1, parsed.hours, parsed.minutes, 0, 0).getTime();
}

export function fromTimePickerTimestamp(
  value: number | null | undefined,
  fmt: TimeFormat = "24"
): string | undefined {
  if (value === null || value === undefined) return undefined;
  const date = new Date(value);
  const displayTime = `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
  return isValidTimeValue(displayTime, fmt) ? displayTime : undefined;
}

/** Build the `dateTimeValidation` payload for the given element per the matrix. */
export function buildDateTimeValidation(element: DateTimeElement): DateTimeValidation {
  const collect = (element.dataType as CollectType) ?? "DATE";

  // TIME: time-of-day restriction only (no daysAllowed).
  if (collect === "TIME") {
    if (element.timeMode === "range") {
      return {
        mode: "range",
        rangeMin: to24(element.timeRangeStart, element.timeRangeStartMeridian, element.timeFormat ?? "24"),
        rangeMax: to24(element.timeRangeEnd, element.timeRangeEndMeridian, element.timeFormat ?? "24"),
      };
    }
    return { mode: "any" };
  }

  // DATE and DATE_TIME share the date-mode state machine.
  const dateMode = element.dateMode ?? "any";

  if (dateMode === "any") {
    return { mode: "any" };
  }

  if (dateMode === "past_only") {
    // UI hides the day picker but the payload always allows all seven.
    return { mode: "past_only", daysAllowed: [...ALL_DAYS] };
  }

  if (dateMode === "range") {
    return {
      mode: "range",
      rangeMin: element.dateRangeMin ?? "",
      rangeMax: element.dateRangeMax ?? "",
      daysAllowed: daysToPayload(element.days),
    };
  }

  // future_only + sub-option.
  const base: DateTimeValidation = {
    mode: "future_only",
    daysAllowed: daysToPayload(element.days),
  };
  if (element.futureSub === "buffer") {
    return { ...base, bufferDays: toInt(element.bufferDays) };
  }
  if (element.futureSub === "rolling") {
    return { ...base, rollingDays: toInt(element.rollingDays) };
  }
  return base;
}

/**
 * Sidebar routing discriminator: only custom DATE/TIME/DATE_TIME fields route
 * to DateTimeSettings; DOB / standard date and other custom field data types
 * stay on their existing settings panels.
 */
export function isCustomDateTimeElement(element?: { custom?: boolean; dataType?: string }): boolean {
  return isDateTimeField({
    custom: element?.custom,
    dataType: element?.dataType,
  });
}

/** True when the element is a custom Date & Time Picker field (DATE/TIME/DATE_TIME). */
export function isDateTimeField(element: { custom?: boolean; dataType?: string }): boolean {
  return !!(
    element.custom &&
    (element.dataType === "DATE" ||
      element.dataType === "TIME" ||
      element.dataType === "DATE_TIME")
  );
}

function isDateTimeDataType(dataType?: string): dataType is CollectType {
  return DATE_TIME_DATA_TYPES.includes(dataType as CollectType);
}

function futureSubFromValidation(validation: DateTimeValidation): FutureSub {
  if (Object.prototype.hasOwnProperty.call(validation, "bufferDays")) return "buffer";
  if (Object.prototype.hasOwnProperty.call(validation, "rollingDays")) return "rolling";
  return "any_future";
}

/**
 * Rebuild transient sidebar state from the persisted validation contract.
 * Persisted form JSON stores `dateTimeValidation`; duplicated control fields
 * are hydrated at load time so the settings UI can keep using simple top-level props.
 */
export function hydrateDateTimeElement<T extends DateTimeElement>(element: T): T {
  const collect = element.dataType as CollectType | undefined;
  if (!isDateTimeDataType(collect)) return element;

  const validation = element.dateTimeValidation ?? buildDateTimeValidation(element);
  const timeFormat = element.timeFormat ?? "24";
  const hydrated: DateTimeElement = {
    ...element,
    dateTimeValidation: validation,
    timeFormat,
    defaultTime: element.defaultTime ?? "none",
  };

  if (collect === "TIME") {
    hydrated.dateMode = undefined;
    hydrated.futureSub = undefined;
    hydrated.dateRangeMin = undefined;
    hydrated.dateRangeMax = undefined;
    hydrated.days = undefined;
    hydrated.timeMode = validation.mode === "range" ? "range" : "any";

    if (validation.mode === "range") {
      const start = from24(validation.rangeMin, timeFormat);
      const end = from24(validation.rangeMax, timeFormat);
      hydrated.timeRangeStart = start.value;
      hydrated.timeRangeStartMeridian = start.meridian;
      hydrated.timeRangeEnd = end.value;
      hydrated.timeRangeEndMeridian = end.meridian;
    } else {
      hydrated.timeRangeStart = undefined;
      hydrated.timeRangeStartMeridian = undefined;
      hydrated.timeRangeEnd = undefined;
      hydrated.timeRangeEndMeridian = undefined;
    }
    return hydrated as T;
  }

  hydrated.timeMode = undefined;
  hydrated.timeRangeStart = undefined;
  hydrated.timeRangeStartMeridian = undefined;
  hydrated.timeRangeEnd = undefined;
  hydrated.timeRangeEndMeridian = undefined;
  hydrated.dateMode = validation.mode;

  if (validation.mode === "future_only") {
    hydrated.futureSub = futureSubFromValidation(validation);
    hydrated.days = payloadToDays(validation.daysAllowed);
    hydrated.bufferDays = validation.bufferDays;
    hydrated.rollingDays = validation.rollingDays;
    hydrated.dateRangeMin = undefined;
    hydrated.dateRangeMax = undefined;
  } else if (validation.mode === "range") {
    hydrated.futureSub = undefined;
    hydrated.dateRangeMin = validation.rangeMin;
    hydrated.dateRangeMax = validation.rangeMax;
    hydrated.days = payloadToDays(validation.daysAllowed);
    hydrated.bufferDays = undefined;
    hydrated.rollingDays = undefined;
  } else {
    hydrated.futureSub = undefined;
    hydrated.days = undefined;
    hydrated.bufferDays = undefined;
    hydrated.rollingDays = undefined;
    hydrated.dateRangeMin = undefined;
    hydrated.dateRangeMax = undefined;
  }

  return hydrated as T;
}

/**
 * Remove hydrateable sidebar-only state before persisting form builder JSON.
 * Unless `includeValidation` is true (location is in the rollout allowlist),
 * the canonical `dateTimeValidation` payload is stripped too so it never
 * reaches any API.
 */
export function stripDateTimeBuilderState<T extends DateTimeElement>(
  element: T,
  includeValidation = false
): T {
  if (!isDateTimeDataType(element.dataType)) return element;
  const stripped = { ...element } as Record<string, unknown>;
  DATE_TIME_BUILDER_STATE_KEYS.forEach((key) => delete stripped[key]);
  if (!includeValidation) delete stripped.dateTimeValidation;
  return stripped as T;
}

/**
 * Identify a Date & Time element in save-blocked messaging. The save gate can
 * fire for a field whose settings panel is closed — or that lives on another
 * slide — so the toast has to name it. Falls back to the unique key when the
 * empty label is itself the reason the field is invalid.
 */
export function dateTimeElementDisplayName(
  element?: { label?: string; hiddenFieldQueryKey?: string; fieldKey?: string }
): string {
  return (
    [element?.label, element?.hiddenFieldQueryKey, element?.fieldKey]
      .map((value) => String(value ?? "").trim())
      .find((value) => value !== "") ?? ""
  );
}

/**
 * Hard save-gate validity for a Date & Time element. Mirrors the live inline
 * errors: blocks on empty label, no weekday when days apply, missing/reversed
 * date range (equal allowed), time end <= start, and invalid custom default time.
 * buffer/rolling never block (they coerce to 0).
 */
export function isDateTimeElementValid(
  element: DateTimeElement & { label?: string }
): boolean {
  if (!String(element.label ?? "").trim()) return false;

  const collect = (element.dataType as CollectType) ?? "DATE";
  const fmt = element.timeFormat ?? "24";
  const customDefaultTimeApplies =
    (collect === "TIME" || collect === "DATE_TIME") && element.defaultTime === "custom";
  if (customDefaultTimeApplies && !isValidTimeValue(element.customTime, fmt)) return false;

  if (collect === "TIME") {
    if (element.timeMode !== "range") return true;
    if (!element.timeRangeStart || !element.timeRangeEnd) return false;
    if (!isValidTimeValue(element.timeRangeStart, fmt) || !isValidTimeValue(element.timeRangeEnd, fmt)) {
      return false;
    }
    const start = to24(element.timeRangeStart, element.timeRangeStartMeridian, fmt);
    const end = to24(element.timeRangeEnd, element.timeRangeEndMeridian, fmt);
    return end > start;
  }

  // DATE / DATE_TIME
  const dateMode = element.dateMode ?? "any";
  if (dateMode === "range") {
    if (!element.dateRangeMin || !element.dateRangeMax) return false;
    if (element.dateRangeMax < element.dateRangeMin) return false;
  }
  if (dateMode === "future_only" || dateMode === "range") {
    const days = element.days ?? {};
    const anyChecked = (["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const).some(
      (k) => days[k] === true
    );
    if (!anyChecked) return false;
  }
  return true;
}

/**
 * Backward-compat migration: seed `dateTimeValidation` + form-only metadata
 * defaults on a DATE/TIME/DATE_TIME element that predates this feature.
 * Idempotent — persisted validation stays canonical and hydrates UI state.
 * Gated on `dataType` only (load-path elements may not carry the `custom` flag yet).
 */
export function ensureDateTimeDefaults<
  T extends DateTimeElement
>(element: T): T {
  return hydrateDateTimeElement(element);
}

/** Compose a canvas/preview display token for the element's collect type. */
export function buildPreviewToken(element: DateTimeElement): string {
  const collect = (element.dataType as CollectType) ?? "DATE";
  if (!isDateTimeDataType(collect)) return "";

  const placeholder = element.placeholder?.trim();
  const datePart = formatDatePreview(element.format, element.separator);
  const timePart = formatTimePreview(element.timeFormat ?? "24");

  if (placeholder && !isGeneratedPreviewPlaceholder(placeholder, collect)) {
    return placeholder;
  }

  if (collect === "TIME") return timePart;
  if (collect === "DATE_TIME") return `${datePart} ${timePart}`;
  return datePart;
}
